import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService, Toast, ToastPackage } from 'ngx-toastr';
import { T } from 'src/assets/i18n/translation-keys';
import { ToastrConfig } from '../../../utilities/toastr-config.utilities';

@Component({
    selector: '[app-custom-toastr]',
    styleUrls: ['./custom-toastr.component.scss'],
    templateUrl: './custom-toastr.component.html',
    preserveWhitespaces: false,
    animations: [
        trigger('flyInOut', [
            state('inactive', style({
                opacity: 0,
            })),
            transition('inactive => active', animate('400ms ease-out', keyframes([
                style({
                    transform: 'translate3d(100%, 0, 0) skewX(-30deg)',
                    opacity: 0,
                }),
                style({
                    transform: 'skewX(20deg)',
                    opacity: 1,
                }),
                style({
                    transform: 'skewX(-5deg)',
                    opacity: 1,
                }),
                style({
                    transform: 'none',
                    opacity: 1,
                }),
            ]))),
            transition('active => removed', animate('400ms ease-out', keyframes([
                style({
                    opacity: 1,
                }),
                style({
                    transform: 'translate3d(100%, 0, 0) skewX(30deg)',
                    opacity: 0,
                }),
            ]))),
        ]),
    ],
    standalone: false
})
export class CustomToastrComponent extends Toast {
  showBtns?: boolean;
  link?: string;
  copyLink?: boolean;
  icon: string = '';

  public readonly T = T;
  public viewText: string = '';
  public copyLinkText: string = '';

  constructor(
    protected toastrService: ToastrService,
    public toastPackage: ToastPackage,
    private readonly router: Router,
    protected readonly translateService: TranslateService,
  ) {
    super();

    const config = this.toastPackage.config as unknown as ToastrConfig;
    if(config?.icon) {
      this.icon = config.icon;
    }

    const data = config?.payload || {};
    this.showBtns = data.showBtns;
    this.link = data.link;
    this.copyLink = data.copyLink;
    this.viewText = this.translateService.instant(T.common.view);
    this.copyLinkText = this.translateService.instant(T.common.copy_link);
  }

  get iconImage() {
    let image: string = '';

    switch (this.toastPackage.toastType) {
      case 'toast-success': {
        image = 'icon-alert-success';
        break;
      }
      case 'toast-error': {
        image = 'icon-alert-error';
        break;
      }
      default: {
        image = 'icon-alert-success';
        break;
      }
    }
    return `assets/images/${image}.png`;
  }

  onView() {
    if (!this.link) return;

    if (this.isExternalUrl(this.link)) {
      window.open(this.link, '_blank', 'noopener,noreferrer');
      return;
    }

    const [path, fragment] = this.link.split('#');

    if (fragment) {
      void this.router.navigate([path], { fragment });
    } else {
      void this.router.navigate([path]);
    }
  }

  private isExternalUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
  }

  onCopyLink(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    if(!this.link) return;

    const baseUrl = window.location.origin;
    const route = this.link.startsWith('/') ? this.link : `/${this.link}`;
    const textToCopy = `${baseUrl}${route}`;

    navigator.clipboard.writeText(textToCopy).then(() => {
      this.remove();
    }).catch(err => {
      console.error('Could not copy text: ', err);
    });
  }
}
